<script>
import http from "@/utilities/http";
import { find, forEach, get, map } from "lodash";
import { mapGetters } from "vuex";

import PProductQuantityPicker from "@/components/forms/PProductQuantityPicker";

const key = (o) => `${o.productId}:${o.treatmentId}:${o.qualityId}`.toUpperCase();

export default {
  name: "PProductQuantitiesPicker",
  props: {
    value: { type: Array, default: () => [] },
    transactionType: { type: String, default: "TO" },
    orderId: { type: String, default: "" },
    isExternal: { type: Boolean, default: false },
    showBalances: { type: Boolean, default: true },
    showPrices: { type: Boolean, default: true },
    allowNegativeVolume: { type: Boolean, default: false },
    showFill: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    // TODO update external order
    // locationId: { type: String, required: true },
    locationTo: { type: Object, required: false },
    truckTypeId: { type: String, default: "" },
    noTransport: { type: Boolean, default: false },
  },
  data() {
    return {
      products: [],
      requestFailed: false,
      isLoading: false,
    };
  },
  components: {
    PProductQuantityPicker
  },
  computed: {
    ...mapGetters(["loginUser", "location", "customer"]),
    isGL() {
      return this.transactionType === "GL";
    },
    isTO() {
      return this.transactionType === "TO";
    },
    isHE() {
      return this.transactionType === "HE";
    },
    loadTriggers() {
      return {
        locationTo: this.locationTo,
        truckTypeId: this.truckTypeId,
        noTransport: this.noTransport,
      };
    },
    // https://dev.azure.com/smartreturdev/Portal/_workitems/edit/35/
    hidePricesSveCatC() {
      return (
        this.customer.abcCode === "C" &&
        this.customer.company === "SVE" &&
        this.isTO
      );
    },
  },
  watch: {
    loadTriggers: {
      handler: async function() {
        await this.getProducts();
      },
      immediate: true,
    },

    value: function(val) {
      forEach(this.products, (p) => {
        const match = find(val, (v) => key(v) === key(p));
        p.quantity = match ? match.quantity : 0;
        p.quantityExtra = match ? match.quantityExtra : 0;
      });
    },
  },
  methods: {
    async getProducts() {
      this.isLoading = true;

      try {
        const params = {
          portalType: "PORTAL",
          transactionType: this.transactionType,
        };

        if (this.isExternal) {
          params.isExternal = this.isExternal;
        }

        if (this.truckTypeId) {
          params.truckTypeId = this.truckTypeId;
        }

        if (this.orderId) {
          params.orderId = this.orderId;
        }

        if (this.noTransport) {
          params.transporterId = "DIREKTE";
        }

        let products = [];

        if (this.isGL) {
          if (
            this.locationTo &&
            this.locationTo.customerId === this.location.customerId
          ) {
            params.transactionType = "OO";
          }

          let poolingProducts = [];
          const commissionProducts = [];

          if (this.locationTo) {
            poolingProducts = await http.get("AvailableProducts", {
              params: {
                ...params,
                locationId: this.location.id,
                isPooling: this.locationTo.isPooling,
                isCommission: this.locationTo.isCommission,
              },
            });
          }

          products = poolingProducts.concat(commissionProducts);
        } else {
          products = await http.get("AvailableProducts", {
            params: {
              ...params,
              locationId: this.location.id,
            },
          });
        }

        this.products = map(products, (product) => {
          return {
            ...product,
            ...this.tryGetQuantityFromValue(product),
          };
        });
      } catch {
        this.requestFailed = true;
      }
      this.isLoading = false;
      // this.$emit("ready")
    },
    setQuantity(product, quantity) {
      product.quantity = quantity;
      // const products = filter(this.products, product => product.quantity > 0)
      this.emitInputEvent();
    },
    emitInputEvent() {
      this.$emit("input", this.products);
    },
    tryGetQuantityFromValue(product) {
      const match = find(this.value, (value) => {
        return key(value) === key(product);
      });
      return {
        quantity: get(match, "quantity", 0),
        quantityExtra: get(match, "quantityExtra", 0),
      };
    },
    numberFormat(number, decimals) {
      return new Intl.NumberFormat(this.$i18n.locale.replace('-sw', ''), {
        minimumFractionDigits: decimals,
      }).format(number);
    },
  },
};
</script>

<template>
  <div>
    <PLoader v-if="isLoading" />

    <template v-else>
      <PProductQuantityPicker
        v-for="(product, index) in products"
        :key="'product-qty-picker__' + index"
        :allowNegativeVolume="allowNegativeVolume"
        :disabled="disabled"
        :hidePricesSveCatC="hidePricesSveCatC"
        :isExternal="isExternal"
        :isGL="isGL"
        :isHE="isHE"
        :isTO="isTO"
        :product="product"
        :showBalances="showBalances"
        :showFill="showFill"
        :showInternalStock="location.showInternalStock"
        :showPrices="showPrices"
        :wrongStepError="$t('PProductQuantityPicker.QuantityStepError')"
        @updateProduct="setQuantity"
      />
      <slot name="location">
        <div v-if="!products.length">Ingen produkter for lokasjon</div>
      </slot>
    </template>
  </div>
</template>
